import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/auth";
import { useLang } from "@/lib/lang";
import { Trophy, TrendingUp, Calendar, Gift } from "lucide-react";

export default function Leaderboard() {
    const { t } = useLang();
    const [period, setPeriod] = useState("week"); // week | month
    const [section, setSection] = useState("score"); // score | sessions
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/leaderboard?period=${period}`)
            .then((r) => setData(r.data))
            .catch(() => setData({ by_score: [], by_sessions: [] }))
            .finally(() => setLoading(false));
    }, [period]);

    const rows = data ? (section === "score" ? data.by_score : data.by_sessions) : [];

    return (
        <div className="min-h-screen bg-ink text-slate-900">
            <Navbar />
            <main className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
                <div className="mono-label mb-3">/ LEADERBOARD</div>
                <h1 className="font-display text-5xl md:text-6xl tracking-tighter mb-10" data-testid="leaderboard-title">
                    {t("leaderboard.title")}
                </h1>

                {/* Period switch: week / month */}
                <div className="flex gap-3 mb-6" data-testid="leaderboard-period-tabs">
                    <button
                        onClick={() => setPeriod("week")}
                        className={`px-5 py-2 border transition-colors mono-label ${period === "week" ? "border-brand text-brand" : "border-slate-300 text-slate-600"}`}
                        data-testid="leaderboard-period-week"
                    >
                        {t("leaderboard.weekly")}
                    </button>
                    <button
                        onClick={() => setPeriod("month")}
                        className={`px-5 py-2 border transition-colors mono-label ${period === "month" ? "border-brand text-brand" : "border-slate-300 text-slate-600"}`}
                        data-testid="leaderboard-period-month"
                    >
                        {t("leaderboard.monthly")}
                    </button>
                </div>

                {/* Monthly prizes panel */}
                {period === "month" && data?.monthly_prizes && (
                    <div className="card-surface p-6 mb-8" data-testid="monthly-prizes-panel">
                        <div className="mono-label mb-4 flex items-center gap-2">
                            <Gift size={14} className="text-brand" /> {t("leaderboard.prizes_title")}
                        </div>
                        <div className="grid grid-cols-3 gap-3 mb-2">
                            {data.monthly_prizes.map((p) => (
                                <div key={p.rank} className="text-center border border-slate-200 py-4" data-testid={`prize-rank-${p.rank}`}>
                                    <div className="font-display text-2xl mb-1">#{p.rank}</div>
                                    <div className="font-mono text-brand text-lg">+{p.reward}</div>
                                    <div className="mono-label text-slate-400 text-[10px]">{t("leaderboard.credits")}</div>
                                </div>
                            ))}
                        </div>
                        <p className="text-xs text-slate-500">{t("leaderboard.prizes_hint")}</p>
                    </div>
                )}

                {/* Previous month winners */}
                {period === "month" && data?.previous_month_winners?.length > 0 && (
                    <div className="mb-10" data-testid="previous-month-winners">
                        <div className="mono-label mb-3">/ {t("leaderboard.prev_winners_title")}</div>
                        <div className="border border-slate-200 divide-y divide-slate-200">
                            {data.previous_month_winners.map((w) => (
                                <div key={w.rank} className="px-5 py-3 grid grid-cols-12 items-center gap-3">
                                    <div className="col-span-1 font-mono text-slate-500">#{w.rank}</div>
                                    <div className="col-span-6 font-display">{w.name}</div>
                                    <div className="col-span-3 font-mono text-slate-500">{w.avg_score}/100</div>
                                    <div className="col-span-2 text-right font-mono text-brand">+{w.reward}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Score / sessions switch */}
                <div className="flex gap-3 mb-6" data-testid="leaderboard-metric-tabs">
                    <button
                        onClick={() => setSection("score")}
                        className={`mono-label px-4 py-2 border transition-colors flex items-center gap-2 ${section === "score" ? "border-brand text-brand" : "border-slate-300 text-slate-600"}`}
                        data-testid="leaderboard-tab-score"
                    >
                        <TrendingUp size={14} /> {t("leaderboard.by_score")}
                    </button>
                    <button
                        onClick={() => setSection("sessions")}
                        className={`mono-label px-4 py-2 border transition-colors flex items-center gap-2 ${section === "sessions" ? "border-brand text-brand" : "border-slate-300 text-slate-600"}`}
                        data-testid="leaderboard-tab-sessions"
                    >
                        <Calendar size={14} /> {t("leaderboard.by_sessions")}
                    </button>
                </div>

                {loading && <p className="text-slate-500">{t("common.loading")}</p>}

                {!loading && rows.length === 0 && (
                    <p className="text-slate-500" data-testid="leaderboard-empty">{t("leaderboard.empty")}</p>
                )}

                {!loading && rows.length > 0 && (
                    <div className="border border-slate-200 divide-y divide-slate-200" data-testid="leaderboard-list">
                        {rows.map((row) => (
                            <div
                                key={row.user_id}
                                className={`px-6 py-4 grid grid-cols-12 gap-4 items-center ${row.is_me ? "bg-brand/5" : ""}`}
                                data-testid={`leaderboard-row-${row.rank}`}
                            >
                                <div className="col-span-2 flex items-center gap-2">
                                    {row.rank <= 3 ? (
                                        <Trophy size={16} className="text-brand" />
                                    ) : (
                                        <span className="mono-label text-slate-400">#{row.rank}</span>
                                    )}
                                    {row.rank <= 3 && <span className="font-mono text-sm text-slate-500">#{row.rank}</span>}
                                </div>
                                <div className="col-span-7 font-display text-lg">
                                    {row.name}
                                    {row.is_me && <span className="mono-label text-brand ml-2">{t("leaderboard.you")}</span>}
                                </div>
                                <div className="col-span-3 text-right font-mono text-brand">
                                    {section === "score" ? `${row.value}/100` : row.value}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}
